/**
 * React component for lazy-loaded videos with a fallback poster image.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.videoImagePath - The URL of the video.
 * @param {boolean} [props.isMobile=false] - Indicates whether the video is for mobile.
 * @param {function} [props.setIsHeroVideoLoaded] - Callback function to set the loading state.
 * @param {boolean} [props.shouldPause] - Indicates whether the video should be paused.
 * @param {string} [props.posterUrl=""] - The URL of the poster image.
 * @param {string} [props.customClasses=""] - Additional CSS classes for styling.
 * @param {boolean} [props.loop=true] - Indicates whether the video should loop.
 * @param {Object} rest - Additional props to be passed to the underlying video element.
 * @returns {JSX.Element} React component.
 */

import { useInView } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";

export function buildUrl(videoPath) {
  if (!videoPath) {
    return "";
  }
  
  if (videoPath.includes("http")) {
    return videoPath;
  } else if (videoPath.includes("public")) {
    return videoPath.replace("public/", "")
  } else {
    return `https:${videoPath}`;
  }
}

function LazyVideoNew(props) {
  const {
    videoImagePath = "",
    isMobile = {},
    setIsHeroVideoLoaded,
    shouldPause,
    posterUrl = "",
    customClasses = "",
    loop = {},
    ...rest
  } = props;
  const ref = useRef();
  const inView = useInView(ref, { amount: 0.001});
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const shouldLoop = loop === false ? false : true;

  const imageSrcUrl = isMobile
    ? buildUrl(videoImagePath?.fields?.mobilePosterImage?.fields?.file?.url)
    : buildUrl(videoImagePath?.fields?.primaryPosterImage?.fields?.file?.url);

  const videoSrcUrl = (inView || videoLoaded)
    ? buildUrl(isMobile ? videoImagePath?.fields.mobileVideo?.fields?.file?.url : videoImagePath?.fields?.primaryVideo?.fields?.file.url)
    : "";

  const handleVideoPlay = () => {
    setVideoLoaded(true);
    if (setIsHeroVideoLoaded) {
      setIsHeroVideoLoaded(true);
    }
  };

  const handleVideoNotPlay = () => {
      if (setIsHeroVideoLoaded) {
        setIsHeroVideoLoaded(true);
      }
  };

  const handleOnPlaying = () => {
    if ( !videoPlaying ) {
      setVideoPlaying(true);
    }
  };

  useEffect(() => {
    if (ref?.current) {
      if (inView) {
        ref.current.play().catch(error => {
          console.log('Video play error:', error);
        });
      } else {
        ref.current?.pause();
      }
    }
  }, [inView, ref?.current]);

  const playVideo = () => {
    if (ref?.current && videoLoaded) {
      ref.current.play().catch(error => {
        console.log('Video play error:', error);
      });
    }
  };
  // const playVideo = () => {
  //   if (ref?.current) {
  //     ref.current.play().catch();
  //   }
  // };
  const pauseVideo = () => {
    if (ref?.current) {
      ref.current.pause();
    }
  };
  useEffect(() => {
    if (shouldPause) {
      pauseVideo();
    } else {
      playVideo();
    }
  }, [shouldPause]);

  

  const getVideo =() => {
    const hasVideoUrl = isMobile
          ? videoImagePath?.fields.mobileVideo?.fields?.file?.url
          : videoImagePath?.fields?.primaryVideo?.fields?.file.url;
    if ( !hasVideoUrl ) {
      return <></>
    }
    return <video
      ref={ref}
      src={videoSrcUrl}
      loop={shouldLoop}
      muted
      playsInline
      autoPlay
      className={`${customClasses} ${videoPlaying ? "visible" : "invisible"}`}
      onPlay={handleVideoPlay}
      onCanPlay={handleVideoNotPlay}
      onError={handleVideoNotPlay}
      onPlaying={handleOnPlaying}
      {...rest}
    ></video>;
  };

  const getImage = () => {
    const hasImageSrc = isMobile ? videoImagePath?.fields?.mobilePosterImage?.fields?.file?.url
    : videoImagePath?.fields?.primaryPosterImage?.fields?.file?.url;
    if (!hasImageSrc) {
      return <></>
    }
    return <Image
      src={imageSrcUrl}
      alt="Poster Image"
      layout="fill"
      className={`${customClasses}  ${videoPlaying ? "-z-[1]" : "z-[0]"}`}
    />;
  }
  return (
    <>
      {getVideo()}
      {getImage()}
    </>
  );
}

export default LazyVideoNew;
